<script lang="ts">
	import type { MultipleLineChartOption } from "@hitit/new_ui/src";
	import { PointIcon, MultipleLineChart } from "@hitit/new_ui";
	import { t, api } from "../../../stores";
	import type { Resolution } from "@hitit/hub/src/v7/utils";
	import { formatISO } from "date-fns";
	import {
		getAllDateInRange,
		getChartDateKey,
		getResolutionOfRange,
		xAxisLabel,
		chartToolTipDateFormat,
		formatKrwNumber,
	} from "@hitit/hub/src/v7/utils";
	import { onDestroy } from "svelte";

	export let storeIds: string[] = [];
	export let range = {
		start: new Date(),
		end: new Date(),
	};
	type ChartData = {
		value: number;
		date: Date;
		class: string;
	};
	let svg: SVGSVGElement;
	let datas: ChartData[] = [];
	let allDates: Date[] = [];
	let __resizeEvent__ = () => {};
	let resolution: Resolution = "HOURLY";
	let chart: HTMLDivElement;
	let tooltip: HTMLDivElement;
	let parseDate = "";
	let newRedeemed = "";
	let returnRedeemed = "";
	const areaGradient = [
		{
			dataClass: "new_redeem",
			stopDatas: [
				{
					offset: "0%",
					style: "stop-color:#e67570;stop-opacity:0.1",
				},
				{
					offset: "90%",
					style: "stop-color:white;stop-opacity:0.1",
				},
			],
		},
		{
			dataClass: "return_redeem",
			stopDatas: [
				{
					offset: "0%",
					style: "stop-color:#bcbfc3;stop-opacity:0.1",
				},
				{
					offset: "90%",
					style: "stop-color:white;stop-opacity:0.1",
				},
			],
		},
	];
	const mousemoveEvent = (event: MouseEvent, datas: ChartData[]) => {
		const { clientX, clientY } = event;

		const [returnData, newData] = datas;
		parseDate = chartToolTipDateFormat(newData.date, resolution);
		newRedeemed = formatKrwNumber(newData.value);
		returnRedeemed = formatKrwNumber(returnData.value);

		tooltip.style.display = "flex";

		const { innerWidth } = window;
		const { width } = tooltip.getBoundingClientRect();
		tooltip.style.top = clientY + 20 + "px";
		if (innerWidth > clientX + 20 + width) {
			tooltip.style.left = clientX + 20 + "px";
		} else {
			let left = clientX - (20 + width);
			if (left < 0) {
				left = left * -1;
			}

			tooltip.style.left = left + "px";
		}
	};
	const mouseoutEvent = (e: MouseEvent) => {
		tooltip.style.display = "none";
	};

	const sorting = (a: string, b: string) => {
		const index = ["return_redeem", "new_redeem"];
		const parseA = index.findIndex((v) => v === a);
		const parseB = index.findIndex((v) => v === b);

		return parseA - parseB;
	};
	function getReport(
		range: { start: Date; end: Date },
		storeId: string,
		resolution: string
	) {
		const { start, end } = range;
		return $api.getRewardReport({
			startTimeInTimeZone: formatISO(start),
			endTimeInTimeZone: formatISO(end),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: resolution,
			storeid: storeId,
		});
	}

	//Do
	$: (async (__storeIds: string[], __range: { start: Date; end: Date }) => {
		try {
			resolution = getResolutionOfRange(range);
			allDates = getAllDateInRange(range, resolution);
			const map = new Map();
			const originStoreIds = storeIds;
			const originRange = range;

			const allRevenue = __storeIds.map((id) =>
				getReport(__range, id, resolution)
			);
			const promises = await Promise.all(allRevenue);
			//
			if (originStoreIds !== __storeIds || originRange !== __range) {
				console.log("Canceld");
				return;
			}

			for (const result of promises) {
				const { redeemedReport, redeemedNewVisitorReport } = result.data;

				for (const report of redeemedNewVisitorReport) {
					const { year, month, day, hour, reward } = report;
					const n_key = `New-${year}-${month}-${day}-${hour}`;
					const originNValue = map.get(n_key) || 0;
					map.set(n_key, originNValue + Math.abs(reward));
				}
				for (const report of redeemedReport) {
					const { year, month, day, hour, reward } = report;
					const a_key = `All-${year}-${month}-${day}-${hour}`;
					const n_key = `New-${year}-${month}-${day}-${hour}`;
					const r_key = `Return-${year}-${month}-${day}-${hour}`;
					const originAValue = map.get(a_key) || 0;
					const originNValue = map.get(n_key) || 0;
					const newAValue = originAValue + Math.abs(reward);
					map.set(a_key, newAValue);
					map.set(r_key, newAValue - originNValue);
				}
			}

			const N = allDates.map((v) => {
				return {
					date: v,
					value: map.get("New-" + getChartDateKey(v, resolution)) || 0,
					class: "new_redeem",
				};
			});

			const R = allDates.map((v) => {
				return {
					date: v,
					value: map.get("Return-" + getChartDateKey(v, resolution)) || 0,
					class: "return_redeem",
				};
			});
			datas = [...N, ...R];
		} catch (error) {
			console.log(error);
		}
	})(storeIds, range);

	$: if (chart && datas) {
		if (svg) {
			svg.remove();
			window.removeEventListener("resize", __resizeEvent__);
		}

		const chartOption: MultipleLineChartOption<ChartData> = {
			classification: "class",
			y: (d: ChartData) => d.value,
			margin: {
				left: 15,
				right: 15,
				top: 15,
				bottom: 40,
			},
			areaGradient,
			xAxislabel: xAxisLabel(resolution, allDates),
			yDomainCorrection: (yMax) => (yMax > 50 ? yMax : 50),
			sorting,
			mousemoveEvent,
			mouseoutEvent,
		};
		const { node, resizeEvent } = MultipleLineChart(chart, datas, chartOption);
		svg = node as SVGSVGElement;
		__resizeEvent__ = resizeEvent;
	}
	onDestroy(() => {
		window.removeEventListener("resize", __resizeEvent__);
	});

</script>

<div class="container">
	<div class="title">
		<div class="icon">
			<PointIcon fill={'#f66d6c'} />
		</div>
		<p>{$t('hub.dashboard.graph.Redeemed_Point_Trend')}</p>
	</div>
	<div class="graph" bind:this={chart} />
</div>
<div id="tooltip" bind:this={tooltip}>
	<div class="date">{parseDate}</div>
	<div class="new box">
		<div class="color-icon" />
		{$t('hub.dashboard.graph.New_Customer') + ' : '}
		{newRedeemed}P
	</div>
	<div class="return box">
		<div class="color-icon" />
		{$t('hub.dashboard.graph.Return_Customer') + ' : '}
		{returnRedeemed}P
	</div>
</div>

<style lang="scss">
	@import "../../../variables.scss";
	#tooltip {
		position: fixed;
		z-index: 9;
		display: none;
		min-width: 200px;
		max-width: calc(100vw - 80px);
		flex-direction: column;
		border-radius: 5px;
		background-color: rgba($color: #46474b, $alpha: 0.7);
		padding: 20px;
		gap: 10px;

		.date {
			color: white;
			font-weight: 550;
			font-size: 1.25rem;
			text-align: center;
		}
		.box {
			display: flex;
			align-items: center;
			font-size: 1.25rem;
			gap: 10px;
			width: 100%;
			color: white;
		}
		.new .color-icon {
			background-color: #f66d6c;
		}
		.return .color-icon {
			background-color: #bcbfc3;
		}

		.color-icon {
			width: 16px;
			height: 16px;
		}
	}

	.container {
		display: flex;
		flex-direction: column;
		box-sizing: border-box;
		width: 100%;
		height: 100%;
		padding: 20px 0px;

		.graph {
			height: 367px;
			:global(svg .axisGroup text) {
				font-size: 0.875rem;
				fill: #6d6f76;
				font-weight: 200;
			}
			:global(svg path#new_redeem) {
				stroke: #f66d6c;
				animation: dash 1s linear forwards;
			}

			:global(svg path#return_redeem) {
				stroke: #bcbfc3;
				animation: dash 1s linear forwards;
			}
			:global(svg circle[dataType="return_redeem"]) {
				transition: all 0.2s;
				fill: white;
				stroke: #bcbfc3;
			}

			:global(svg circle[dataType="new_redeem"]) {
				transition: all 0.2s;
				fill: white;
				stroke: #f66d6c;
			}
			:global(svg circle[dataType="new_redeem"].show) {
				stroke: #f66d6c;
				fill: #f66d6c;
				opacity: 1 !important;
			}
			:global(svg circle[dataType="new_redeem"].hide) {
				opacity: 0;
				fill: #f66d6c;
			}
			:global(svg line[dataType="new_redeem"]) {
				stroke: #f66d6c;
			}
			/*  */
			:global(svg circle[dataType="return_redeem"]) {
				transition: all 0.2s;
				fill: white;
				stroke: #bcbfc3;
			}
			:global(svg circle[dataType="return_redeem"].show) {
				stroke: #bcbfc3;
				fill: #bcbfc3;
				opacity: 1 !important;
			}
			:global(svg circle[dataType="return_redeem"].hide) {
				opacity: 0;
				fill: #bcbfc3;
			}
			:global(svg line[dataType="return_redeem"]) {
				stroke: #bcbfc3;
			}

			:global(svg path#area-new_redeem),
			:global(svg path#area-return_redeem) {
				opacity: 0;
				animation-delay: 1s;
				animation-duration: 1s;
				animation-name: showup;
				animation-fill-mode: forwards;
			}
		}

		.title {
			width: 100%;
			box-sizing: border-box;
			padding: 0px 10px;
			display: flex;
			gap: 10px;

			.icon {
				width: 12.7px;
				height: 19px;
			}
			p {
				font-weight: 550;
				color: #46474b;
			}
		}
	}

	@include DesktopSize {
		.container {
			padding: 30px 0px;
			.graph {
				height: 412px;
			}
			.title {
				padding: 0px 30px;
				gap: 15.5px;

				.icon {
					width: 17.4px;
					height: 26px;
				}
				p {
					font-size: 1.25rem;
				}
			}
		}
	}
	@keyframes dash {
		to {
			stroke-dashoffset: 0;
		}
	}
	@keyframes showup {
		to {
			opacity: 1;
		}
	}

</style>
